import React, { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { requestPasswordReset } from '../../services/authService';

/**
 * Forgot Password Form Component
 * Implements US-1.1.4: Password Reset
 */
export default function ForgotPasswordForm({ setAuthMode }) {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Backend returns the same response whether or not the account exists
      // (prevents email enumeration).
      await requestPasswordReset(email);
      setResetEmailSent(true);
    } catch (err) {
      setError(err.message || 'Unable to process your request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (resetEmailSent) {
    return (
      <div className="py-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold mb-2">Check your email</h3>
        <p className="text-gray-600 mb-6">
          If an account exists with <strong>{email}</strong>, you will receive password reset instructions within 5 minutes.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Did not receive the email? Check your spam folder or{' '}
          <button
            type="button"
            onClick={() => setResetEmailSent(false)}
            className={`${theme.textPrimary} ${theme.textHover} font-medium`}
          >
            try again
          </button>
        </p>
        <button
          type="button"
          onClick={() => {
            setAuthMode('signin');
            setResetEmailSent(false);
          }}
          className={`${theme.textPrimary} ${theme.textHover} font-semibold`}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
          placeholder="john@example.com"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          We will send reset instructions to this email
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full ${theme.buttonPrimary} py-3 rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? 'Sending...' : 'Send Reset Link'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setAuthMode('signin')}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          ← Back to sign in
        </button>
      </div>
    </form>
  );
}
